import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  doc,
  getDoc,
  writeBatch,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase';

const sharedGroupsRef = collection(db, 'sharedGroups');

/**
 * Share a group with another user
 */
export const shareGroup = async (fromUserId, toUserId, groupName, withEditAccess = false) => {
  try {
    const batch = writeBatch(db);
    const cardsRef = collection(db, 'cards');
    
    // Get all cards in the group
    const groupCards = await getDocs(
      query(cardsRef, where('group', '==', groupName))
    );

    // Check if user has edit access to all cards they're trying to share with edit
    if (withEditAccess) {
      const hasEditAccess = await Promise.all(
        groupCards.docs.map(async (cardDoc) => {
          const cardData = cardDoc.data();
          return cardData.userId === fromUserId || 
                 (Array.isArray(cardData.canEdit) && 
                  cardData.canEdit.includes(fromUserId));
        })
      );

      if (!hasEditAccess.every(Boolean)) {
        throw new Error('You do not have edit access to all cards in this group');
      }
    }

    // Update permissions for each card
    groupCards.forEach(cardDoc => {
      const cardRef = doc(db, 'cards', cardDoc.id);
      if (withEditAccess) {
        batch.update(cardRef, {
          canEdit: arrayUnion(toUserId),
          canView: arrayUnion(toUserId),
          sharedGroups: arrayUnion(groupName)
        });
      } else {
        batch.update(cardRef, {
          canView: arrayUnion(toUserId),
          sharedGroups: arrayUnion(groupName)
        });
      }
    });

    // Add a specific group share record
    const sharedGroupRef = doc(collection(db, 'sharedGroups'));
    batch.set(sharedGroupRef, {
      fromUserId,
      toUserId,
      groupName,
      accessLevel: withEditAccess ? 'edit' : 'view',
      sharedAt: serverTimestamp(),
      status: 'active',
      cardCount: groupCards.size
    });

    await batch.commit();
  } catch (error) {
    console.error('Error sharing group:', error);
    throw error;
  }
};

/**
 * Get all groups shared with a user
 */
export const getSharedGroups = async (userId, otherUserId = null) => {
  try {
    if (otherUserId) {
      // Get groups shared between two specific users
      const [sharedToQuery, sharedFromQuery] = await Promise.all([
        getDocs(query(sharedGroupsRef,
          where('fromUserId', '==', userId),
          where('toUserId', '==', otherUserId),
          where('status', '==', 'active')
        )),
        getDocs(query(sharedGroupsRef,
          where('fromUserId', '==', otherUserId),
          where('toUserId', '==', userId),
          where('status', '==', 'active')
        ))
      ]);

      return [...sharedToQuery.docs, ...sharedFromQuery.docs]
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
    } else {
      // Get all groups shared with this user
      const sharedQuery = await getDocs(query(sharedGroupsRef,
        where('toUserId', '==', userId),
        where('status', '==', 'active')
      ));

      return sharedQuery.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }
  } catch (error) {
    console.error('Error getting shared groups:', error);
    throw error;
  }
};

/**
 * Get all groups a user can access (owned + shared)
 */
export const getAccessibleGroups = async (userId) => {
  try {
    const groups = new Set();
    
    // Get groups from owned cards
    const ownedCardsQuery = query(collection(db, 'cards'), 
      where('userId', '==', userId)
    );
    const ownedCards = await getDocs(ownedCardsQuery);
    ownedCards.forEach(doc => {
      const data = doc.data();
      if (data.group && !data.deleted) {
        groups.add(data.group);
      }
    });

    // Get groups from shared cards that still have view access
    const sharedCardsQuery = query(collection(db, 'cards'),
      where('canView', 'array-contains', userId)
    );
    const sharedCards = await getDocs(sharedCardsQuery);
    sharedCards.forEach(doc => {
      const data = doc.data();
      if (data.group && !data.deleted && data.sharedGroups?.includes(data.group)) {
        groups.add(data.group);
      }
    });

    return Array.from(groups);
  } catch (error) {
    console.error('Error getting accessible groups:', error);
    throw error;
  }
};

/**
 * Check if a user has access to a specific group
 */
export const hasGroupAccess = async (userId, groupName) => {
  try {
    // Check if user owns any cards in this group
    const cardsRef = collection(db, 'cards');
    const userGroupCardsQuery = query(cardsRef,
      where('userId', '==', userId),
      where('group', '==', groupName)
    );
    const userGroupCards = await getDocs(userGroupCardsQuery);
    
    if (!userGroupCards.empty) {
      return true;
    }

    // Check if group is shared with user
    const sharedQuery = query(sharedGroupsRef,
      where('toUserId', '==', userId),
      where('groupName', '==', groupName),
      where('status', '==', 'active')
    );
    const sharedSnapshot = await getDocs(sharedQuery);
    
    return !sharedSnapshot.empty;
  } catch (error) {
    console.error('Error checking group access:', error);
    throw error;
  }
};

export const shareCard = async (fromUserId, toUserId, card, withEditAccess = false) => {
  try {
    // Check if sharer has edit access
    const cardRef = doc(db, 'cards', card.id);
    const cardDoc = await getDoc(cardRef);
    const cardData = cardDoc.data();
    
    const canGrantEditAccess = cardData.userId === fromUserId || 
                              (Array.isArray(cardData.canEdit) && 
                               cardData.canEdit.includes(fromUserId));

    // If trying to grant edit access without having it
    if (withEditAccess && !canGrantEditAccess) {
      throw new Error('You do not have permission to grant edit access');
    }

    const batch = writeBatch(db);
    
    // Update card permissions
    if (withEditAccess && canGrantEditAccess) {
      batch.update(cardRef, {
        canEdit: arrayUnion(toUserId),
        canView: arrayUnion(toUserId)
      });
    } else {
      batch.update(cardRef, {
        canView: arrayUnion(toUserId)
      });
    }
    
    // Record share in sharedItems
    const sharedItemRef = collection(db, 'sharedItems');
    batch.set(doc(sharedItemRef), {
      type: 'card',
      cardId: card.id,
      fromUserId,
      toUserId,
      accessLevel: withEditAccess ? 'edit' : 'view',
      sharedAt: serverTimestamp(),
      status: 'active'
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error sharing card:', error);
    throw error;
  }
};

export const getSharedCards = async (userId, otherUserId) => {
  try {
    console.log('Getting shared cards for user:', userId, 'and other user:', otherUserId);
    const cardsRef = collection(db, 'cards');
    const sharedCardsQuery = query(
      cardsRef,
      where('canView', 'array-contains', userId)
    );
    
    const snapshot = await getDocs(sharedCardsQuery);
    const sharedCards = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })).filter(card => 
      // Only include cards from the specified user if otherUserId is provided
      !otherUserId || card.userId === otherUserId
    );
    
    console.log('Found shared cards:', sharedCards);
    return sharedCards;
  } catch (error) {
    console.error('Error getting shared cards:', error);
    return [];
  }
};

export const unshareGroup = async (fromUserId, toUserId, groupName) => {
  try {
    const batch = writeBatch(db);
    
    // 1. Get all cards in the group
    const cardsRef = collection(db, 'cards');
    const groupCardsQuery = query(
      cardsRef,
      where('userId', '==', fromUserId),
      where('groups', 'array-contains', groupName)
    );
    
    const groupCards = await getDocs(groupCardsQuery);
    const cardIds = groupCards.docs.map(doc => doc.id);

    if (cardIds.length > 0) {
      // 2. Remove all shared items for these cards
      const sharedItemsRef = collection(db, 'sharedItems');
      const sharedItemsQuery = query(
        sharedItemsRef,
        where('toUserId', '==', toUserId),
        where('cardId', 'in', cardIds)
      );
      
      const sharedItems = await getDocs(sharedItemsQuery);
      sharedItems.forEach(doc => {
        batch.delete(doc.ref);
      });

      // 3. Update card permissions and remove from sharedGroups
      groupCards.forEach(cardDoc => {
        const cardData = cardDoc.data();
        const updatedSharedGroups = (cardData.sharedGroups || []).filter(g => g !== groupName);
        
        batch.update(cardDoc.ref, {
          canEdit: arrayRemove(toUserId),
          canView: arrayRemove(toUserId),
          sharedGroups: updatedSharedGroups
        });
      });
    }

    // 4. Remove from sharedGroups collection
    const sharedGroupsRef = collection(db, 'sharedGroups');
    const sharedGroupQuery = query(
      sharedGroupsRef,
      where('fromUserId', '==', fromUserId),
      where('toUserId', '==', toUserId),
      where('groupName', '==', groupName)
    );
    
    const sharedGroupDocs = await getDocs(sharedGroupQuery);
    sharedGroupDocs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error unsharing group:', error);
    throw error;
  }
};

export const unshareCard = async (fromUserId, toUserId, cardId) => {
  try {
    const batch = writeBatch(db);
    const cardRef = doc(db, 'cards', cardId);
    const sharedItemsRef = collection(db, 'sharedItems');

    // Get shared items for this card
    const sharedItems = await getDocs(
      query(sharedItemsRef, 
        where('fromUserId', '==', fromUserId),
        where('toUserId', '==', toUserId),
        where('cardId', '==', cardId)
      )
    );

    // Delete shared items records
    sharedItems.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Remove sharing permissions
    batch.update(cardRef, {
      canEdit: arrayRemove(toUserId),
      canView: arrayRemove(toUserId)
    });

    await batch.commit();
  } catch (error) {
    console.error('Error unsharing card:', error);
    throw error;
  }
}; 